import { type ActivityTypesEnum, type FetchSocialFeedMeByFeedSlugParams, useLocale } from '@capsicum/common';
import { mySociosFeedKeys } from '@capsicum/socios-core';
import { useAtomValue } from 'jotai';

import { favouriteTeamFeedFiltersAtom, useSocialFeedMeQuery } from '../data-access';
import { type FeedFiltersMapType } from '../models';
import { clearFeedPages, prepareFeedData } from '../util';

type UseFavouriteTeamFeedProps = {
	fetchAllPages: boolean;
	filtersMap: FeedFiltersMapType;
};

/**
 * @warning DON'T USE this hook directly! use the one implemented on useFavouriteTeamFeedWEB or useFavouriteTeamFeedMOB files instead.
 */
export const useFavouriteTeamFeed = ({ fetchAllPages = true, filtersMap }: UseFavouriteTeamFeedProps) => {
	const { locale } = useLocale();
	const { selectedFilters } = useAtomValue(favouriteTeamFeedFiltersAtom);
	const key = [...mySociosFeedKeys.favouriteTeam, selectedFilters];
	const activityTypes = selectedFilters
		.map((x: any) => filtersMap.get(x) || [])
		.flat() as unknown as Array<ActivityTypesEnum>;

	const params: FetchSocialFeedMeByFeedSlugParams = {
		'activityTypes[]': activityTypes,
		feedSlug: 'todo_favorite',
		limit: 10,
		locale: locale
	};

	const {
		data,
		error,
		fetchNextPage,
		hasNextPage,
		isFetched,
		isFetching,
		isLoading,
		refetch: refetchQuery
	} = useSocialFeedMeQuery(key, params);

	const { feedData, metadata } = prepareFeedData(data);

	if (fetchAllPages) {
		if (hasNextPage && !isFetching) {
			void fetchNextPage();
		}
	}

	const refetch = async () => {
		clearFeedPages([key]);
		await refetchQuery();
	};

	return {
		data: feedData,
		error,
		fetchNextPage,
		hasNextPage,
		isFetched,
		isFetching,
		isLoading,
		metadata,
		refetch
	};
};
